import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { AlertPosition, AlertType } from '@domain/enums/alert/alert-type.enum';
import { AuthGateway } from '@domain/models/auth/gateway/auth.gateway';
import { environment } from '@environments/environment';
import { AlertService } from '../alert-service/alert.service';
import { bindFeatureFlagBypassToken, resetFeatureFlagBypassToken } from '@infraestructure/helpers/feature-flags/feature-flag-bypass.helper';
import { TokenService } from './token.service';
import { CypherService } from '../cypher-service/cypher.service';
import { IAuthRequest, IAuthResponse, IForgotPasswordRequest, IResetPasswordRequest } from '@domain/interfaces/auth/auth-request.interface';
import { JwtHelperService } from '@auth0/angular-jwt';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IS_PUBLIC } from '@infraestructure/helpers/auth/auth.interceptor';
import { LoadingType } from '@domain/enums/alert/loading-type.enum';
import { UserAddressTypeEnum } from '@domain/enums/user/address-type.enum';
import { User } from '@domain/models/account-settings/user.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IAPIResponse } from '@domain/interfaces/apis/api-response.interface';
import { BrandResourcesUseCase } from '@domain/usecases/brand/brand-resources.usecase';
import { Brand } from '@domain/enums/brand/brand.enum';
import { clearMemberToken, saveMemberToken } from '@infraestructure/helpers/auth/member-token.storage';
import { CreditScoreStateService } from '../credit-score-array/credit-score-state.service';

export type PasswordTokenMode = 'set' | 'reset';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends AuthGateway {
  private readonly http = inject(HttpClient);
  private readonly _router = inject(Router);
  private readonly _jwtHelper = inject(JwtHelperService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _creditScoreState = inject(CreditScoreStateService);
  private readonly CONTEXT = { context: new HttpContext().set(IS_PUBLIC, true) };
  private readonly TOKEN_EXPIRY_THRESHOLD_MINUTES = 5;
  private readonly FORGOT_PASSWORD_WARNING_CODES = [161, 162];

  private _member!: BehaviorSubject<User>;

  constructor(
    private _tokenService: TokenService,
    private _alertService: AlertService,
    private _cypherService: CypherService,
    private _modalService: NgbModal,
    private _brandResourcesUseCase: BrandResourcesUseCase,
  ) {
    super();
    const currentUserStorage = this._tokenService.getUser();
    this._member = new BehaviorSubject<User>(currentUserStorage!);
  }

  get user(): WritableSignal<User | null> {
    const token = localStorage.getItem('auth-token');
    return signal(token ? this._jwtHelper.decodeToken(token) : null);
  }

  public setMember(member: User): void {
    bindFeatureFlagBypassToken(member?.id);
    this._member?.next(member);
    this._tokenService.saveUser(member);
  }

  isAuthenticated(): boolean {
    return !this._jwtHelper.isTokenExpired();
  }

  login(authRequest: IAuthRequest, memberPortalServiceURL: string): Observable<IAuthResponse> {
    this._alertService.setLoading(true, LoadingType.Default, ['Loading...']);
    let encryptedPassword = this._cypherService.encryptText(authRequest.password);
    return this.http
      .post<IAuthResponse>(
        `${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/auth/login`,
        { email: authRequest.email?.toLowerCase(), password: encryptedPassword },
        this.CONTEXT
      )
      .pipe(
        map((authResponse: IAuthResponse) => {
          this._alertService.setLoading(false);
          if (authResponse?.responseCode == 100) {
            var user = authResponse.user;
            const member: User = {
              id: user?.id,
              brand: user?.brand,
              affiliateId: user?.affiliateId,
              everflowSub1: user?.everflowSub1,
              everflowSub4: user?.everflowSub4,
              everflowRealAffiliateId: user?.everflowRealAffiliateId,
              personalInformation: {
                id: user?.id,
                email: user?.email,
                firstName: user?.firstName,
                lastName: user?.lastName,
                phone: user?.phone,
                shippingAddress: user?.userAddresses?.find((a) => a.addressType == UserAddressTypeEnum.Primary),
              },
              loginCount: user?.loginCount,
              notificationCounter: user?.notificationCounter,
              activeReward: user?.activeReward,
              mx: user?.userMX ?? {},
              products: user?.userProducts ?? [],
              isNewMemberPortal: user?.isNewMemberPortal
            };
            this.setMember(member);
            this._tokenService.saveToken(authResponse?.accessToken!);
            saveMemberToken(authResponse?.memberToken, authResponse?.memberTokenExpiresAtUTC);
            this.scheduleTokenRefresh(authResponse?.accessToken?.accessToken!);

            this._brandResourcesUseCase.getBrandResources(Brand[member?.brand!]).pipe(first()).subscribe({
              next: () => {
                location.reload();
              },
              error: (error: any) => {},
            });
          }
          return authResponse;
        }),
        catchError((e) => {
          this._alertService.setLoading(false);
          this._alertService.showAlert({
            type: AlertType.Warning,
            title: 'Oops!',
            description: e?.error?.Message,
            position: AlertPosition.TopCenter,
          });
          return of();
        })
      );
  }

  forgotPassword(forgotPassword: IForgotPasswordRequest): Observable<IAPIResponse> {
    this._alertService.setLoading(true, LoadingType.Default, ['Sending Email...']);
    forgotPassword.email = forgotPassword?.email?.toLowerCase();
    return this.http
      .post<IAPIResponse>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/auth/forgot-password`, forgotPassword, this.CONTEXT)
      .pipe(
        map((apiResponse: IAPIResponse) => {
          this._alertService.setLoading(false);
          const isSuccess = apiResponse.responseCode == 151;
          const isWarning = this.FORGOT_PASSWORD_WARNING_CODES.includes(apiResponse.responseCode!);
          this._alertService.showAlert({
            type: isSuccess ? AlertType.Success : isWarning ? AlertType.Warning : AlertType.Error,
            title: isSuccess ? 'Success' : isWarning ? 'Warning' : 'Error',
            description: apiResponse?.message,
            position: AlertPosition.TopCenter,
          });
          return apiResponse;
        }),
        catchError((e) => {
          console.log(e);
          this._alertService.setLoading(false);
          const isWarning = this.FORGOT_PASSWORD_WARNING_CODES.includes(e?.error?.responseCode);
          this._alertService.showAlert({
            type: isWarning ? AlertType.Warning : AlertType.Error,
            title: isWarning ? 'Warning' : 'Error',
            description: e?.error?.message,
            position: AlertPosition.TopCenter,
          });
          return of();
        })
      );
  }

  /** 160 valid, 157 invalid, 158 expired, 159 used. Never consumes the token. */
  validatePasswordToken(mode: PasswordTokenMode, token: string): Observable<IAPIResponse> {
    const route = mode === 'set' ? 'set-password/validate' : 'validate-reset-token';
    return this.postPasswordToken(route, { token }, 'Validating link...');
  }

  /** 155 set; 163 the password breaks the rule; 153, 154, 159 for the token. */
  submitPassword(mode: PasswordTokenMode, token: string, password: string): Observable<IAPIResponse> {
    const route = mode === 'set' ? 'set-password' : 'reset-password';
    return this.postPasswordToken(route, { token, password: this._cypherService.encryptText(password) }, 'Saving password...');
  }

  // Emits the API's answer for errors too: the page decides what an invalid or expired link shows.
  private postPasswordToken(route: string, body: IResetPasswordRequest, loadingText: string): Observable<IAPIResponse> {
    this._alertService.setLoading(true, LoadingType.Default, [loadingText]);
    return this.http
      .post<IAPIResponse>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/auth/${route}`, body, this.CONTEXT)
      .pipe(
        catchError((e) => of<IAPIResponse>(e?.error?.responseCode ? e.error : { responseCode: 0, message: '' })),
        tap(() => this._alertService.setLoading(false))
      );
  }

  refreshToken(): Observable<IAuthResponse | null> {
    const refreshToken = localStorage.getItem('refresh-token');
    if (!refreshToken) {
      return of();
    }

    return this.http
      .post<IAuthResponse>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/auth/token/refresh`, { refreshToken }, this.CONTEXT)
      .pipe(
        catchError(() => of()),
        tap((authResponse: IAuthResponse) => {
          this._tokenService.saveToken(authResponse?.accessToken!);
          this.scheduleTokenRefresh(authResponse?.accessToken?.accessToken!);
        })
      );
  }

  scheduleTokenRefresh(token: string): void {
    const expirationTime = this._jwtHelper.getTokenExpirationDate(token)?.getTime();
    const refreshTime = expirationTime ? expirationTime - this.TOKEN_EXPIRY_THRESHOLD_MINUTES * 60 * 1000 : Date.now();
    const refreshInterval = refreshTime - Date.now();

    if (refreshInterval > 0) {
      setTimeout(() => {
        this.refreshToken().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      }, refreshInterval);
    }
  }

  logout(): void {
    // const refresh-token = localStorage.getItem('refresh-token');
    // this.http.post<IAuthResponse>(`${environment.memberPortalServiceURL.replace('{{brandDomain}}', window.location.host)}/auth/token/invalidate`, {refresh-token}, this.CONTEXT)
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe(() => {
    this._modalService.dismissAll();
    resetFeatureFlagBypassToken();
    localStorage.removeItem('auth-user');
    localStorage.removeItem('auth-token');
    localStorage.removeItem('refresh-token');
    localStorage.removeItem('mx');
    localStorage.removeItem('RewardProgramToken');
    clearMemberToken();
    this._creditScoreState.reset();
    this._router.navigate(['/login']);
    // });
  }

  public getUser(): Observable<User> {
    return this._member?.asObservable().pipe();
  }
}
